import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';

import { useUpdateAudioPreferences } from '../../../../common/hooks/useUpdateAudioPreferences';
import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useFlags } from '../../../context/FlagsProviderContext';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';

import { AudioControlTriggerButton } from './sub-components/AudioControlButton';

const i18n = defineMessages({
	playbackSpeedLabel: {
		id: 'audio.miniplayer-playback-speed-control.playback-speed-label',
		defaultMessage: 'Playback speed',
		description:
			'The label is used as a tooltip and for accessibility for the select dropdown which provides playback speed options for the actively playing audio',
	},
	speedLabel08x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-08x',
		defaultMessage: '0.8x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 0.8x',
	},
	speedLabel10x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-10x',
		defaultMessage: '1x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.0x',
	},
	speedLabel12x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-12x',
		defaultMessage: '1.2x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.2x',
	},
	speedLabel15x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-15x',
		defaultMessage: '1.5x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.5x',
	},
	speedLabel18x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-18x',
		defaultMessage: '1.8x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.8x',
	},
	speedLabel20x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-20x',
		defaultMessage: '2x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 2.0x',
	},
	errorTitle: {
		id: 'audio.miniplayer-playback-speed-control.error-title',
		defaultMessage: 'Playback speed error',
		description: 'Error title shown when playback speed cannot be changed',
	},
	errorDescription: {
		id: 'audio.miniplayer-playback-speed-control.error-description',
		defaultMessage: 'Unable to save speed preferences. Please try again.',
		description: 'Error description shown when playback speed cannot be changed',
	},
});

export type PlaybackSpeedControlProps = {
	onClick?: (speed: number) => void;
};

export const PlaybackSpeedControl = ({ onClick }: PlaybackSpeedControlProps): React.ReactNode => {
	const intl = useIntl();
	const { getCustomization, setPlaybackSpeed } = useAudioPlayerStore();
	const { updateAudioPreferences } = useUpdateAudioPreferences();
	const { audioRef } = useAudioPlayerStreamingContext();
	const flags = useFlags();

	const PLAYBACK_RATES = useMemo(
		() =>
			new Map([
				[0.8, intl.formatMessage(i18n.speedLabel08x)],
				[1.0, intl.formatMessage(i18n.speedLabel10x)],
				[1.2, intl.formatMessage(i18n.speedLabel12x)],
				[1.5, intl.formatMessage(i18n.speedLabel15x)],
				[1.8, intl.formatMessage(i18n.speedLabel18x)],
				[2.0, intl.formatMessage(i18n.speedLabel20x)],
			]),
		[intl],
	);

	const handleClick = useCallback(
		(speed: number) => {
			if (audioRef.current) {
				try {
					audioRef.current.playbackRate = speed;
					setPlaybackSpeed(speed);

					onClick?.(speed);

					updateAudioPreferences({
						...getCustomization(),
						playbackSpeed: speed,
					});
				} catch {
					// Some browsers or conditions may make playbackRate read-only
					flags?.showFlag({
						appearance: 'error',
						title: intl.formatMessage(i18n.errorTitle),
						description: intl.formatMessage(i18n.errorDescription),
						close: 'auto',
					});
				}
			}
		},
		[audioRef, getCustomization, setPlaybackSpeed, updateAudioPreferences, onClick, flags, intl],
	);

	return (
		<DropdownMenu
			placement="top-start"
			trigger={({ triggerRef, ...triggerProps }) => (
				<Flex justifyContent="center">
					<Tooltip content={intl.formatMessage(i18n.playbackSpeedLabel)}>
						<AudioControlTriggerButton
							triggerProps={triggerProps}
							triggerRef={triggerRef}
							label={intl.formatMessage(i18n.playbackSpeedLabel)}
						>
							<Text color={'color.text.subtle'} weight="medium">
								{PLAYBACK_RATES.get(getCustomization().playbackSpeed)}
							</Text>
						</AudioControlTriggerButton>
					</Tooltip>
				</Flex>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{Array.from(PLAYBACK_RATES.entries()).map(([value, label]) => (
					<DropdownItem key={value} onClick={() => handleClick(value)}>
						{label}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
